import type {
  CameraState,
  NumericRange,
  ScalarGrid,
  XyzDataPoint,
} from '../types';
import type { CollapsedSample } from '../data/collapseCoincident';
import { toBinIndex } from '../data/toScalarGrid';
import type { LatticeFrame } from '../data/horizontalAxes';
import { clampPitch, getCameraBasis, type Projector } from './projection';

export type Point3 = readonly [x: number, y: number, z: number];

/** One occupied bin of a `binned` grid, in field units. */
export type BinnedColumn = {
  /** Lattice index, `gridY * width + gridX`. */
  index: number;
  gridX: number;
  gridY: number;
  /** The bin's extent along x, centered on its lattice position. */
  xBounds: NumericRange;
  yBounds: NumericRange;
  /** The cell's aggregate, as `toScalarGrid` computed it. */
  value: number;
  /** How many components fall in the bin. */
  count: number;
  points: XyzDataPoint[];
};

/** A column as an axis-aligned box in the camera's cube: x and y in [-1, 1], z in [0, 1]. */
export type ColumnBox = {
  column: BinnedColumn;
  x: NumericRange;
  y: NumericRange;
  z: NumericRange;
};

/** Where the camera looks from, in cube coordinates with z not yet scaled by the relief. */
export type Viewer =
  | { kind: 'orthographic'; forward: Point3 }
  | { kind: 'perspective'; eye: Point3 };

export type ColumnFace = {
  normal: Point3;
  corners: readonly [Point3, Point3, Point3, Point3];
};

/** The fraction of its bin a column's footprint covers along each axis. */
export const COLUMN_FOOTPRINT = 0.8;

type BinGroup = { index: number; samples: CollapsedSample[] };

const groupByBin = (
  samples: CollapsedSample[],
  grid: ScalarGrid
): BinGroup[] => {
  const groups = samples.reduce((byIndex, sample) => {
    const index = toBinIndex({
      sample,
      xRange: grid.dataXRange,
      yRange: grid.dataYRange,
      counts: { x: grid.width, y: grid.height },
    });
    if (index === null) return byIndex;
    const existing = byIndex.get(index);
    if (existing) {
      existing.push(sample);
      return byIndex;
    }
    byIndex.set(index, [sample]);
    return byIndex;
  }, new Map<number, CollapsedSample[]>());

  return Array.from(groups, ([index, grouped]) => ({
    index,
    samples: grouped,
  })).sort((a, b) => a.index - b.index);
};

const toBinBounds = (
  position: number,
  [min, max]: NumericRange,
  binCount: number
): NumericRange => {
  const step = (max - min) / (binCount - 1);
  const center = min + position * step;
  return [center - step / 2, center + step / 2];
};

const toColumn = (
  grid: ScalarGrid,
  { index, samples }: BinGroup
): BinnedColumn | null => {
  const value = grid.values[index];
  if (value === null || value === undefined) return null;

  const column = index % grid.width;
  const row = Math.floor(index / grid.width);

  return {
    index,
    gridX: column,
    gridY: row,
    xBounds: toBinBounds(column, grid.dataXRange, grid.width),
    yBounds: toBinBounds(row, grid.dataYRange, grid.height),
    value,
    count: samples.reduce((total, sample) => total + sample.count, 0),
    points: samples.flatMap(sample => sample.points),
  };
};

const isColumn = (column: BinnedColumn | null): column is BinnedColumn =>
  column !== null;

/**
 * The occupied bins of a `binned` grid, with the components behind each.
 *
 * `samples` must be the collapse the grid was built from.
 */
export const toBinnedColumns = (
  grid: ScalarGrid,
  samples: CollapsedSample[]
): BinnedColumn[] => {
  if (grid.width < 2 || grid.height < 2) return [];

  return groupByBin(samples, grid)
    .map(group => toColumn(grid, group))
    .filter(isColumn);
};

/** A column as the sample a host's hover callback receives: the bin's center and its aggregate. */
export const toColumnSample = (column: BinnedColumn): CollapsedSample => ({
  x: (column.xBounds[0] + column.xBounds[1]) / 2,
  y: (column.yBounds[0] + column.yBounds[1]) / 2,
  z: column.value,
  count: column.count,
  points: column.points,
});

const clampUnit = (value: number): number => Math.max(-1, Math.min(1, value));

const clamp01 = (value: number): number => Math.max(0, Math.min(1, value));

/** The height range a column chart's z axis must cover: the data's, stretched to include 0. */
export const toColumnHeightRange = ([min, max]: NumericRange): NumericRange => [
  Math.min(0, min),
  Math.max(0, max),
];

/** Each column as a box standing on the 0 level of the height axis, its footprint clamped to the plot. */
export const toColumnBoxes = (
  columns: BinnedColumn[],
  grid: ScalarGrid,
  frame: LatticeFrame
): ColumnBox[] => {
  const [zMin, zMax] = grid.zRange;
  const zSpan = zMax - zMin || 1;
  const baseline = clamp01((0 - zMin) / zSpan);
  const half = COLUMN_FOOTPRINT / 2;

  return columns.map(column => {
    const top = (column.value - zMin) / zSpan;
    return {
      column,
      x: [
        clampUnit(frame.toCubeX(column.gridX - half)),
        clampUnit(frame.toCubeX(column.gridX + half)),
      ],
      y: [
        clampUnit(frame.toCubeY(column.gridY - half)),
        clampUnit(frame.toCubeY(column.gridY + half)),
      ],
      z: [Math.min(baseline, top), Math.max(baseline, top)],
    };
  });
};

/**
 * The camera as a point or a direction in cube coordinates.
 *
 * `createProjector`'s foreshortening is a pinhole at `forward / perspective` in relief-scaled space.
 */
export const toViewer = (camera: CameraState): Viewer => {
  const { forward } = getCameraBasis(camera.yaw, clampPitch(camera.pitch));
  if (camera.perspective <= 0) return { kind: 'orthographic', forward };

  const { perspective, heightScale } = camera;
  return {
    kind: 'perspective',
    eye: [
      forward[0] / perspective,
      forward[1] / perspective,
      forward[2] / (perspective * heightScale),
    ],
  };
};

const toLatticeCoordinate = (
  cube: number,
  toCube: (lattice: number) => number
): number => {
  const origin = toCube(0);
  const slope = toCube(1) - origin;
  return slope === 0 ? 0 : (cube - origin) / slope;
};

/** Larger is nearer the camera. */
const toNearness = (
  viewer: Viewer,
  frame: LatticeFrame
): ((box: ColumnBox) => number) => {
  if (viewer.kind === 'orthographic') {
    const [forwardX, forwardY] = viewer.forward;
    return ({ column }) => forwardX * column.gridX + forwardY * column.gridY;
  }

  const eyeColumn = toLatticeCoordinate(viewer.eye[0], frame.toCubeX);
  const eyeRow = toLatticeCoordinate(viewer.eye[1], frame.toCubeY);
  return ({ column }) =>
    -((column.gridX - eyeColumn) ** 2 + (column.gridY - eyeRow) ** 2);
};

/** The boxes far to near for the painter's algorithm, by lattice distance from the camera. */
export const toPaintingOrder = (
  boxes: ColumnBox[],
  viewer: Viewer,
  frame: LatticeFrame
): ColumnBox[] => {
  const nearness = toNearness(viewer, frame);
  return boxes
    .map(box => ({ box, nearness: nearness(box) }))
    .sort((a, b) => a.nearness - b.nearness)
    .map(({ box }) => box);
};

type Axis = 0 | 1 | 2;

const isFacing = (
  viewer: Viewer,
  axis: Axis,
  sign: 1 | -1,
  plane: number
): boolean => {
  if (viewer.kind === 'orthographic') return sign * viewer.forward[axis] > 0;
  return sign * (viewer.eye[axis] - plane) > 0;
};

const toFaces = ({ x, y, z }: ColumnBox): ColumnFace[] => {
  const [x0, x1] = x;
  const [y0, y1] = y;
  const [z0, z1] = z;

  return [
    {
      normal: [0, 0, 1],
      corners: [
        [x0, y0, z1],
        [x1, y0, z1],
        [x1, y1, z1],
        [x0, y1, z1],
      ],
    },
    {
      normal: [0, 0, -1],
      corners: [
        [x0, y0, z0],
        [x0, y1, z0],
        [x1, y1, z0],
        [x1, y0, z0],
      ],
    },
    {
      normal: [1, 0, 0],
      corners: [
        [x1, y0, z0],
        [x1, y1, z0],
        [x1, y1, z1],
        [x1, y0, z1],
      ],
    },
    {
      normal: [-1, 0, 0],
      corners: [
        [x0, y0, z0],
        [x0, y0, z1],
        [x0, y1, z1],
        [x0, y1, z0],
      ],
    },
    {
      normal: [0, 1, 0],
      corners: [
        [x0, y1, z0],
        [x0, y1, z1],
        [x1, y1, z1],
        [x1, y1, z0],
      ],
    },
    {
      normal: [0, -1, 0],
      corners: [
        [x0, y0, z0],
        [x1, y0, z0],
        [x1, y0, z1],
        [x0, y0, z1],
      ],
    },
  ];
};

const toAxis = ([nx, ny]: Point3): Axis => {
  if (nx !== 0) return 0;
  if (ny !== 0) return 1;
  return 2;
};

/** The faces of a box turned toward the camera: never more than three, and never overlapping. */
export const toVisibleFaces = (box: ColumnBox, viewer: Viewer): ColumnFace[] =>
  toFaces(box).filter(face => {
    const axis = toAxis(face.normal);
    const sign = face.normal[axis] > 0 ? 1 : -1;
    return isFacing(viewer, axis, sign, face.corners[0][axis]);
  });

type ScreenPoint = { screenX: number; screenY: number };

const isInsideConvex = (
  polygon: ScreenPoint[],
  { x, y }: { x: number; y: number }
): boolean => {
  const crossings = polygon.map((from, at) => {
    const to = polygon[(at + 1) % polygon.length];
    return (
      (to.screenX - from.screenX) * (y - from.screenY) -
      (to.screenY - from.screenY) * (x - from.screenX)
    );
  });
  return (
    crossings.every(cross => cross >= 0) || crossings.every(cross => cross <= 0)
  );
};

export type FindColumnArgs = {
  /** Far to near, as {@link toPaintingOrder} returns it. */
  order: ColumnBox[];
  viewer: Viewer;
  project: Projector;
  cursor: { x: number; y: number };
};

/** The frontmost column drawn under the cursor. */
export const findColumnAt = ({
  order,
  viewer,
  project,
  cursor,
}: FindColumnArgs): BinnedColumn | null => {
  const hit = order.findLast(box =>
    toVisibleFaces(box, viewer).some(face =>
      isInsideConvex(
        face.corners.map(([x, y, z]) => project(x, y, z)),
        cursor
      )
    )
  );
  return hit?.column ?? null;
};
